import React, { useState } from 'react';
import AdminFilters from './AdminFilters';
import { EmptyState } from '../common';
import { formatDate, formatPhone } from '../../utils/formatters';
import { ROLE_OPTIONS, USER_STATUSES } from '../../constants/admin';
import api from '../../services/api';

const AdminUsersTab = ({
  users,
  loading,
  filters,
  setFilters,
  onSearch,
  onEdit,
  onDelete,
  onCreateUser,
  onBulkDelete,
  onBulkStatusChange
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(users.map(user => user.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (userId) => {
    if (selectedIds.includes(userId)) {
      setSelectedIds(selectedIds.filter(id => id !== userId));
    } else {
      setSelectedIds([...selectedIds, userId]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length > 0 && onBulkDelete) {
      onBulkDelete(selectedIds);
      setSelectedIds([]);
    }
  };

  const handleBulkStatusChange = () => {
    if (selectedIds.length > 0 && bulkStatus && onBulkStatusChange) {
      onBulkStatusChange(selectedIds, bulkStatus);
      setSelectedIds([]);
      setBulkStatus('');
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/admin/users/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const isAllSelected = users.length > 0 && selectedIds.length === users.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < users.length;

  const renderBulkActionsBar = () => {
    if (selectedIds.length === 0) return null;

    return (
      <div className="bulk-actions-bar">
        <span className="selected-count">
          {selectedIds.length} user{selectedIds.length > 1 ? 's' : ''} selected
        </span>
        <div className="bulk-action-buttons">
          <select
            className="bulk-status-select"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
          >
            <option value="">Change Status...</option>
            {USER_STATUSES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            className="btn-bulk-status"
            onClick={handleBulkStatusChange}
            disabled={!bulkStatus}
          >
            Apply
          </button>
          <button className="btn-bulk-delete" onClick={handleBulkDelete}>
            Delete Selected
          </button>
          <button className="btn-clear-selection" onClick={clearSelection}>
            Clear
          </button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    if (users.length === 0) {
      const hasFilters = filters.search || filters.role || filters.status;
      return (
        <EmptyState
          icon="👥"
          title="No users found"
          message={hasFilters
            ? "No users match your current filters. Try adjusting your search criteria."
            : "No users in the system yet. Create your first user to get started."}
          action={!hasFilters ? {
            label: "+ Create User",
            onClick: onCreateUser
          } : null}
        />
      );
    }

    return (
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="checkbox-cell">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={el => {
                    if (el) el.indeterminate = isSomeSelected;
                  }}
                  onChange={handleSelectAll}
                  title="Select all"
                />
              </th>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Phone</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={selectedIds.includes(user.id) ? 'selected' : ''}>
                <td className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(user.id)}
                    onChange={() => handleSelectOne(user.id)}
                  />
                </td>
                <td>{user.id}</td>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>{user.role}</span>
                </td>
                <td>
                  <span className={`status-badge ${user.status || 'active'}`}>
                    {user.status || 'active'}
                  </span>
                </td>
                <td>{user.phone ? formatPhone(user.phone) : '-'}</td>
                <td>{formatDate(user.created_at)}</td>
                <td className="actions">
                  <button className="btn-edit" onClick={() => onEdit(user)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => onDelete(user.id, user.name)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div className="admin-actions-bar">
        <AdminFilters
          filters={filters}
          setFilters={setFilters}
          onSearch={onSearch}
          searchPlaceholder="Search by name or email..."
          filterOptions={ROLE_OPTIONS}
          filterKey="role"
          filterPlaceholder="All Roles"
          secondFilterOptions={USER_STATUSES}
          secondFilterKey="status"
          secondFilterPlaceholder="All Statuses"
        />
        <div className="admin-action-buttons">
          <button className="btn-export" onClick={handleExport}>
            Export CSV
          </button>
          <button className="btn-create" onClick={onCreateUser}>
            + Create User
          </button>
        </div>
      </div>
      {renderBulkActionsBar()}
      {renderContent()}
    </>
  );
};

export default AdminUsersTab;
